import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AuthLayout } from "@/components/auth-layout";
import { Button, Field } from "@/components/kit";
import { useToast } from "@/components/toast";

const FIELDS = [
  { key: "last_name", label: "Nom", placeholder: "Dupont", ac: "family-name" },
  { key: "first_name", label: "Prénom", placeholder: "Marie", ac: "given-name" },
  { key: "email", label: "E-mail", placeholder: "marie@agence.fr", type: "email", ac: "email" },
  { key: "password", label: "Mot de passe (8 caractères min.)", placeholder: "••••••••", type: "password", ac: "new-password" },
  { key: "phone", label: "Téléphone", placeholder: "06 12 34 56 78", type: "tel", ac: "tel" },
  { key: "agency", label: "Nom de l'agence ou du réseau", placeholder: "Agence du Centre", ac: "organization" },
  { key: "city", label: "Ville", placeholder: "Lyon", ac: "address-level2" },
  { key: "department", label: "Département", placeholder: "69 - Rhône" },
  { key: "referral_code", label: "Code de parrainage (facultatif)", placeholder: "MMSXXXXXX" },
];

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const toast = useToast();
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const [form, setForm] = useState(Object.fromEntries(FIELDS.map((f) => [f.key, ""])));
  const [errors, setErrors] = useState({});
  const [inviter, setInviter] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!ref) return;
    const code = String(ref).toUpperCase();
    setForm((p) => ({ ...p, referral_code: code }));
    api(`/referral/${encodeURIComponent(code)}`).then(setInviter).catch(() => setInviter(null));
  }, [ref]);

  const submit = async (e) => {
    e?.preventDefault();
    const errs = {};
    FIELDS.forEach((f) => {
      if (f.key !== "referral_code" && !form[f.key].trim()) errs[f.key] = "Champ obligatoire";
    });
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email.trim())) errs.email = "E-mail invalide";
    if (form.password && form.password.length < 8) errs.password = "8 caractères minimum";
    setErrors(errs);
    setError("");
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      const body = { ...form, email: form.email.trim() };
      if (!body.referral_code.trim()) delete body.referral_code;
      await register(body);
      toast("Compte créé ! Un e-mail de confirmation vous a été envoyé.");
      navigate("/", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Inscription partenaire" subtitle="Gratuit et sans engagement" testID="register-screen">
      {inviter ? (
        <div data-testid="register-inviter-banner" className="rounded-xl border border-[#D4AF37] bg-[#292415] p-3">
          <p className="text-sm text-white">
            Vous êtes invité(e) par {inviter.first_name}
            {inviter.agency ? ` (${inviter.agency})` : ""}. Le code de parrainage est déjà rempli.
          </p>
        </div>
      ) : null}
      <p className="t-body">Indépendants, mandataires et agences de toute la France : créez votre compte en 1 minute.</p>
      <form onSubmit={submit} noValidate className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {FIELDS.map((f) => (
          <div key={f.key} className={["email", "password", "referral_code"].includes(f.key) ? "md:col-span-2" : ""}>
            <Field
              testID={`register-${f.key.replace("_", "-")}-input`}
              label={f.label}
              placeholder={f.placeholder}
              value={form[f.key]}
              error={errors[f.key]}
              type={f.type ?? "text"}
              autoComplete={f.ac}
              className={f.key === "referral_code" ? "uppercase" : ""}
              onChangeText={(t) => {
                setForm((p) => ({ ...p, [f.key]: f.key === "referral_code" ? t.toUpperCase() : t }));
                if (errors[f.key]) setErrors((x) => ({ ...x, [f.key]: undefined }));
              }}
            />
          </div>
        ))}
        {error ? <p data-testid="register-error" role="alert" className="whitespace-pre-line text-sm text-[#EB5757] md:col-span-2">{error}</p> : null}
        <Button testID="register-submit-button" type="submit" title="Créer mon compte" size="lg" loading={loading} className="md:col-span-2" />
      </form>
      <p className="t-caption text-center">
        En créant un compte, vous acceptez que vos données soient traitées par Ma Maison Suivie pour la gestion de vos recommandations et commissions.
      </p>
    </AuthLayout>
  );
}
